import { useState, useEffect } from "react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronDown, Dna, Shield, Microscope, Sparkles } from "lucide-react";
import Lenis from "lenis";
import PageMeta from "../components/PageMeta";


const TEST_TIERS = [
  {
    name: "Baseline",
    tagline: "The starting point for every furchild",
    icon: Shield,
    price: "Included in Steadfast",
    colour: "hsl(338,38%,60%)",
    gradient: "from-pink-50 to-rose-100",
    what: [
      "Core genetic health markers",
      "Breed-specific disease risk flags",
      "Drug sensitivity screening",
      "Inbreeding coefficient estimate",
      "Ancestry & breed composition",
    ],
    forWhom: "Every new Furbaby member receives a Baseline screen. It gives our veterinary team the genetic context to personalise every aspect of your furchild's care from day one.",
  },
  {
    name: "Advanced",
    tagline: "Deep-dive into complex health trajectories",
    icon: Dna,
    price: "Included in Devoted",
    colour: "hsl(338,52%,42%)",
    gradient: "from-rose-50 to-pink-200",
    popular: true,
    what: [
      "Expanded panel: 200+ genetic markers",
      "Cardiac & orthopaedic risk mapping",
      "Metabolic & endocrine predispositions",
      "Coat & trait genetics",
      "Dietary sensitivity & supplement guidance",
      "Longevity risk index",
    ],
    forWhom: "The Advanced Panel is the standard for members who want to stay ahead — catching risk early, before it manifests. Our team uses results to build a prevention protocol specific to your pet.",
  },
  {
    name: "Full Workup",
    tagline: "Complete genomic picture, nothing withheld",
    icon: Microscope,
    price: "Included in Besotted",
    colour: "hsl(248,40%,22%)",
    gradient: "from-violet-50 to-purple-100",
    what: [
      "Whole-genome sequencing analysis",
      "Rare variant & novel mutation detection",
      "Microbiome-genomics integration",
      "Epigenetic age estimation",
      "Personalised longevity roadmap",
      "Annual genomic re-evaluation",
      "Dedicated 60-min genomics consult",
    ],
    forWhom: "The Full Workup delivers everything we know how to do — a complete biological picture with a dedicated consultation to walk through every finding and build a lifelong care strategy.",
  },
];

const FAQS = [
  {
    q: "How is FurGenomics different from consumer DNA tests?",
    a: "Consumer kits deliver breed estimates and basic trait reports. FurGenomics is a clinical-grade panel designed specifically for veterinary use — interpreted by trained veterinary geneticists, integrated with your pet's full health record, and translated directly into care decisions at Furbaby.",
  },
  {
    q: "How is the sample collected?",
    a: "A simple cheek swab, collected during your pet's first wellness visit. No needles, no stress. Most pets barely notice. Results take 4–6 weeks from sample dispatch.",
  },
  {
    q: "Are FurGenomics panels built for Asian pets?",
    a: "Yes. Most veterinary genomics panels are validated against North American and European breed populations. FurGenomics panels are calibrated specifically for the breeds most common in Singapore and Southeast Asia — meaning the allele frequencies, disease expressions, and risk thresholds reflect your furchild's actual background.",
  },
  {
    q: "Will my pet need to be re-tested over time?",
    a: "Your pet's DNA doesn't change — but our ability to interpret it does. Besotted members receive an annual genomic re-evaluation as new research emerges. All members can request updated interpretation reports when significant new findings are published for their breed.",
  },
  {
    q: "Is FurGenomics available for all species?",
    a: "At launch, FurGenomics supports dogs and cats with clinically validated panels. Rabbit, bird, and exotic companion panels are in active development and will be added as the programme expands.",
  },
  {
    q: "I'm not a member — can I still access FurGenomics?",
    a: "FurGenomics is currently exclusive to Furbaby members. It is woven into our care model rather than sold as a standalone product — because the value is in how your veterinary team uses the results, not the report alone.",
  },
];

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border/30">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full text-left flex items-start justify-between gap-4 py-6 group"
      >
        <span className="font-serif text-xl md:text-2xl leading-snug group-hover:opacity-70 transition-opacity duration-300">
          {q}
        </span>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="flex-shrink-0 mt-1"
        >
          <ChevronDown className="w-5 h-5 opacity-40" />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="font-sans text-base opacity-65 leading-relaxed pb-6 max-w-3xl">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FurGenomics() {
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyDone, setNotifyDone] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifyError, setNotifyError] = useState<string | null>(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.02, smoothWheel: true });
    let rafId: number;
    function raf(t: number) { lenis.raf(t); rafId = requestAnimationFrame(raf); }
    rafId = requestAnimationFrame(raf);
    return () => { cancelAnimationFrame(rafId); lenis.destroy(); };
  }, []);

  async function handleNotify(e: React.FormEvent) {
    e.preventDefault();
    setNotifyLoading(true);
    setNotifyError(null);
    try {
      const res = await fetch("/api/furgenomics/notify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: notifyEmail }),
      });
      if (!res.ok) throw new Error("Failed to register");
      setNotifyDone(true);
    } catch {
      setNotifyError("Something went wrong. Please try again.");
    } finally {
      setNotifyLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden selection:bg-primary/40">
      <PageMeta
        title="FurGenomics — Precision Genomics for Pets | Furbaby"
        description="Know your furchild's genome. Furbaby FurGenomics delivers breed-specific disease risk panels, pharmacogenomics screening, and longevity biomarkers — veterinary-grade precision genomics for Singapore pets."
        canonical="https://furbaby.vet/main/furgenomics"
        ogTitle="FurGenomics — Know Your Furchild's Genome"
        ogDescription="Precision genomic testing for cats and dogs. Breed panels, pharmacogenomics, longevity biomarkers — veterinary-grade, built for Singapore."
      />

      <nav className="fixed top-0 left-0 right-0 z-50 px-6 md:px-12 py-6 flex justify-between items-center">
        <Link href="/" className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500">
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform duration-500" />
          Furbaby
        </Link>
        <Link href="/membership" className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500">
          Membership
          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-500" />
        </Link>
      </nav>

      <section className="relative flex flex-col justify-end px-4 md:px-20 pt-32 pb-16 md:pb-28 min-h-[70vh] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-violet-50/40 via-background to-primary/8 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none opacity-20"
          style={{ backgroundImage: "radial-gradient(circle at 70% 30%, hsl(248,60%,85%) 0%, transparent 55%), radial-gradient(circle at 20% 80%, hsl(338,60%,88%) 0%, transparent 50%)" }}
        />
        <div className="relative z-10 max-w-5xl">
          <motion.p
            className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-6"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            FurGenomics · Precision Genomics
          </motion.p>
          <div className="overflow-hidden mb-8">
            <motion.h1
              className="font-serif text-[12vw] md:text-[7vw] leading-[0.88] tracking-tighter"
              initial={{ y: "110%" }} animate={{ y: "0%" }}
              transition={{ duration: 1.3, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            >
              Know your<br /><span className="italic text-primary">furchild's genome.</span>
            </motion.h1>
          </div>
          <motion.p
            className="font-sans text-base md:text-xl max-w-2xl leading-relaxed opacity-65"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 0.65, y: 0 }}
            transition={{ duration: 1, delay: 0.7 }}
          >
            FurGenomics is Furbaby's clinical genomics programme — a veterinary-grade DNA analysis that shapes every care decision your pet receives at our clinic. Not a consumer kit. Not a novelty. A clinical instrument.
          </motion.p>
        </div>
      </section>

      <section className="py-16 md:py-24 px-4 md:px-20 bg-muted/20 border-y border-border/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-4">Built for Singapore</p>
            <h2 className="font-serif text-5xl md:text-7xl tracking-tighter leading-none mb-6">
              Science built<br /><span className="italic text-primary">for this region.</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            >
              <p className="font-sans text-lg leading-relaxed opacity-70 mb-6">
                Most veterinary genomics panels were validated against North American and European breed populations. If your dog was bred in Southeast Asia, that data is a poor fit — the allele frequencies are different, the disease expressions differ, and the risk thresholds need recalibration.
              </p>
              <p className="font-sans text-base leading-relaxed opacity-55 mb-6">
                FurGenomics panels are calibrated specifically for the breeds most commonly kept in Singapore and the region. Results are interpreted against population data relevant to your furchild's actual background — not a proxy cohort from the other side of the world.
              </p>
              <p className="font-sans text-base leading-relaxed opacity-55">
                Our panels evolve as research advances. New disease associations get added. Interpretation improves. Your pet benefits from an ongoing relationship with the science, not a one-time snapshot.
              </p>
            </motion.div>
            <motion.div
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            >
              {[
                { stat: "200+", label: "Genetic markers in our Advanced panel" },
                { stat: "SG", label: "Panels calibrated for Singapore breeds" },
                { stat: "Asian", label: "Pet population data — built for this region" },
                { stat: "Clinical", label: "Veterinary-grade, not consumer-grade" },
              ].map((item, i) => (
                <div key={i} className="p-6 rounded-2xl bg-background border border-border/40">
                  <p className="font-serif text-3xl text-primary mb-2">{item.stat}</p>
                  <p className="font-sans text-xs opacity-60 leading-snug">{item.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-32 px-4 md:px-20 bg-background">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="mb-12 md:mb-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-4">Breed-Specific Disease Panels</p>
            <h2 className="font-serif text-5xl md:text-7xl tracking-tighter leading-none">
              Not guesswork.<br /><span className="italic text-primary">Your pet's own DNA.</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {TEST_TIERS.map((tier, i) => {
              const Icon = tier.icon;
              return (
                <motion.div
                  key={tier.name}
                  className={`relative rounded-[2rem] p-8 overflow-hidden bg-gradient-to-br ${tier.gradient} border border-white/40`}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.9, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                >
                  {tier.popular && (
                    <span className="absolute top-6 right-6 font-grotesk text-[8px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/50">
                      Most detailed
                    </span>
                  )}
                  <Icon className="w-7 h-7 mb-6 opacity-60" style={{ color: tier.colour }} strokeWidth={1.5} />
                  <p className="font-grotesk text-[9px] uppercase tracking-widest opacity-50 mb-1">{tier.price}</p>
                  <h3 className="font-serif text-3xl mb-1" style={{ color: tier.colour }}>{tier.name}</h3>
                  <p className="font-sans text-xs opacity-50 mb-6">{tier.tagline}</p>
                  <ul className="flex flex-col gap-2.5 mb-6">
                    {tier.what.map(w => (
                      <li key={w} className="flex items-start gap-2.5 font-sans text-sm">
                        <span className="block w-1 h-1 rounded-full mt-2 flex-shrink-0 opacity-60" style={{ backgroundColor: tier.colour }} />
                        <span className="opacity-70 leading-snug">{w}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="font-sans text-xs opacity-55 leading-relaxed border-t border-black/10 pt-5">{tier.forWhom}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            className="p-8 md:p-12 rounded-[2rem] bg-muted/30 border border-border/40"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <Sparkles className="w-7 h-7 text-primary mb-6 opacity-60" strokeWidth={1.5} />
            <h3 className="font-serif text-4xl mb-4">How breed panels work</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { step: "01", title: "Sample", body: "A cheek swab is collected at your first visit. No sedation, no needles — most pets are indifferent to it." },
                { step: "02", title: "Analysis", body: "Your sample is processed against our breed-specific panel, validated for the genetic variants most relevant to your pet's breed and regional background." },
                { step: "03", title: "Integration", body: "Results are added to your pet's permanent health file. Your veterinary team reviews them and uses them to shape every care decision going forward." },
              ].map(s => (
                <div key={s.step}>
                  <p className="font-grotesk text-xs text-primary opacity-60 mb-3">{s.step}</p>
                  <h4 className="font-serif text-2xl mb-2">{s.title}</h4>
                  <p className="font-sans text-sm opacity-60 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-32 px-4 md:px-20 bg-background border-t border-border/30">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="font-grotesk text-[10px] uppercase tracking-[0.3em] text-primary mb-4">FAQ</p>
            <h2 className="font-serif text-5xl md:text-6xl tracking-tighter leading-none">
              Questions, answered.
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {FAQS.map(faq => (
              <FAQItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-32 px-4 md:px-20 bg-primary/8 border-t border-primary/20">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <Dna className="w-10 h-10 text-primary mx-auto mb-6 opacity-60" strokeWidth={1.5} />
            <h2 className="font-serif text-5xl md:text-6xl tracking-tighter leading-none mb-4">
              Be the first to know<br />when FurGenomics launches.
            </h2>
            <p className="font-sans text-base opacity-60 mb-8 leading-relaxed">
              FurGenomics launches with Furbaby's clinic opening. Leave your email and we'll notify you — no newsletters, just the announcement.
            </p>
            {notifyDone ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="inline-flex items-center gap-3 font-grotesk text-sm uppercase tracking-widest text-primary"
              >
                <span className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-primary-foreground text-xs">✓</span>
                </span>
                You're on the list
              </motion.div>
            ) : (
              <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email" required value={notifyEmail}
                  onChange={e => setNotifyEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="flex-1 bg-background border border-border/40 rounded-full px-5 py-3 font-sans text-sm focus:outline-none focus:border-primary transition-colors duration-300 placeholder:opacity-40"
                />
                <button
                  type="submit" disabled={notifyLoading}
                  className="bg-primary text-primary-foreground font-grotesk text-xs uppercase tracking-widest px-6 py-3 rounded-full hover:opacity-85 transition-opacity duration-300 disabled:opacity-50 whitespace-nowrap"
                >
                  {notifyLoading ? "Sending…" : "Notify me"}
                </button>
              </form>
            )}
            {notifyError && <p className="font-sans text-sm text-destructive mt-3">{notifyError}</p>}
            <p className="font-sans text-xs opacity-35 mt-4">
              We send one email. No marketing. No follow-ups.
            </p>
          </motion.div>
        </div>
      </section>

      <footer className="py-12 md:py-20 px-4 md:px-20 bg-primary border-t border-primary/30 flex flex-col md:flex-row justify-between items-center gap-8">
        <div>
          <span className="font-serif text-3xl font-bold italic tracking-tighter">Furbaby</span>
          <p className="font-grotesk text-xs uppercase tracking-widest opacity-50 mt-2">© 2025 Furbaby Vet Care · Singapore</p>
        </div>
        <div className="flex gap-8 font-grotesk text-xs uppercase tracking-widest">
          <Link href="/" className="hover:opacity-60 transition-opacity duration-300">Home</Link>
          <Link href="/membership" className="hover:opacity-60 transition-opacity duration-300">Membership</Link>
          <Link href="/book" className="hover:opacity-60 transition-opacity duration-300">Join Waitlist</Link>
        </div>
      </footer>
    </div>
  );
}
